import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CloudUploadOutlined as UploadIcon,
  DescriptionOutlined as FileIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useLookups } from '../../hooks/useLookups';
import {
  findLookupItem,
  getDocumentTypeRules,
  validateDocumentDates,
} from '../../utils/documentTypeFields';
import { lookupLabel } from '../../utils/mandatoryDocumentTypes';

const DirectorDocumentFormDialog = ({
  open,
  onClose,
  onSave,
  presetDocumentType = null,
  existingDoc = null,
}) => {
  const { items: documentTypes } = useLookups('DIRECTOR_DOCUMENT_TYPE');
  const typeLocked = Boolean(presetDocumentType?.code || presetDocumentType?.label);

  const [typeCode, setTypeCode] = useState('');
  const [typeLabel, setTypeLabel] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const selectedType = useMemo(
    () => findLookupItem(documentTypes, typeCode, typeLabel),
    [documentTypes, typeCode, typeLabel]
  );
  const fieldRules = useMemo(() => getDocumentTypeRules(selectedType), [selectedType]);

  useEffect(() => {
    if (!open) return;
    const presetCode = presetDocumentType?.code || '';
    const presetLabel = presetDocumentType?.label || lookupLabel(documentTypes, presetCode);
    setTypeCode(existingDoc?.typeCode || existingDoc?.type || presetCode);
    setTypeLabel(existingDoc?.typeLabel || presetLabel);
    setDocumentNumber(existingDoc?.documentNumber || '');
    setIssueDate(existingDoc?.issueDate || '');
    setExpiryDate(existingDoc?.expiryDate || '');
    setFile(existingDoc?.file || null);
    setError(null);
  }, [open, presetDocumentType, existingDoc, documentTypes]);

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }
    setFile(uploadedFile);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
    maxSize: 20 * 1024 * 1024,
  });

  const handleSave = () => {
    if (!typeCode && !typeLabel) {
      setError('Document type is required');
      return;
    }
    if (fieldRules.requiresDocumentNumber && !documentNumber.trim()) {
      setError('Please enter the document number');
      return;
    }
    const dateError = validateDocumentDates({ issueDate, expiryDate, rules: fieldRules });
    if (dateError) {
      setError(dateError);
      return;
    }
    if (!file) {
      setError('Please choose a file');
      return;
    }

    onSave({
      typeCode: typeCode || selectedType?.code,
      type: typeCode || selectedType?.code,
      typeLabel: typeLabel || selectedType?.label,
      documentNumber: documentNumber.trim(),
      issueDate,
      expiryDate,
      file,
      fileName: file.name,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingDoc ? 'Replace document' : 'Upload document'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {typeLocked ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
              Document type
            </Typography>
            <Chip label={typeLabel || typeCode} color="primary" variant="outlined" />
          </Box>
        ) : (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Document type</InputLabel>
            <Select
              value={typeCode}
              label="Document type"
              onChange={(e) => {
                const code = e.target.value;
                setTypeCode(code);
                setTypeLabel(lookupLabel(documentTypes, code));
              }}
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          required={fieldRules.requiresDocumentNumber}
          label={fieldRules.requiresDocumentNumber ? 'Document number *' : 'Document number (optional)'}
          value={documentNumber}
          onChange={(e) => setDocumentNumber(e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label={fieldRules.requiresIssueDate ? 'Issue date *' : 'Issue date (optional)'}
          type="date"
          value={issueDate}
          onChange={(e) => setIssueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
        />

        <TextField
          fullWidth
          label={fieldRules.requiresExpiry ? 'Expiry date *' : 'Expiry date (optional)'}
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          helperText={fieldRules.requiresExpiry ? 'Required for this document type' : undefined}
        />

        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            mt: 2,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : '#E0E0E0',
            bgcolor: isDragActive ? 'primary.light' : file ? '#E8F5E9' : '#FAFAFA',
            cursor: 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ textAlign: 'center' }}>
            {file ? (
              <>
                <FileIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
                <Typography variant="body2" fontWeight={500}>{file.name}</Typography>
              </>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2">Drag & drop or click to choose file</Typography>
              </>
            )}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!file}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DirectorDocumentFormDialog;
